import { AppointmentProvider } from '../../../context/Appointment/appointment';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { Heading } from '../../../controls/Heading/Heading';
import { useLocale, useLocalization } from '../../../context/Localization/localization';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import { default as css } from './CheckInDetailsLanding.module.scss';
import store from 'store2';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import {
    getServiceDetails,
    IAppointment,
    IServiceDetail,
    IXtimeServiceBase,
    LocalizedTransportationOption,
    MaintenanceMenu,
    RNDService,
    ServiceComment,
    useLocalizedServiceFactory,
    useTransportationOptions,
    XTimeServiceProperties,
} from '../../../API/GoMoto';
import { DMSType, TransportSubtype } from '../../../API/Enums';
import { useFormatAppointmentDate } from '../../../helpers/Appointment';
import { useDisplayLocalizedVehicle } from '../../../helpers/displayVehicle';
import { Spacer } from '../../../controls/Spacer/Spacer';
import { formatAsCurrency, formatAsPhoneNumber } from '../../../helpers/Number';
import { useEffect, useState } from 'react';
import { GMCapitalize, GMUppercase } from '../../../controls/Case';
import { useLocalizedDayOfWeek } from '../../../helpers/dayOfweek';
import { convertHoursTimeString } from '../../../helpers/dateTime';
import { getRndServiceDisplayName } from '../../../helpers/rndServiceDisplayName';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { GoMotoComponent } from '../../../API/GoMoto.types';
import { GMGrid } from '../../../controls/Grid';
import { GMLink } from '../../../controls/Link';
import { Locales, LocalizationType } from '../../../context/Localization/localization.types';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import { ReminderHours } from '../../../context/Kiosk/kiosk.types';

export const CheckInDetailsLanding: GoMotoComponent = () => {
    const Localization: LocalizationType = useLocalization();
    const Locale: Locales = useLocale();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const localizedDayOfWeek = useLocalizedDayOfWeek();
    const serviceFactory = useLocalizedServiceFactory();
    const formatAppointmentDate = useFormatAppointmentDate({
        checkInCompleteFormat: true,
    });
    const {
        key: kioskKey,
        dealer: {
            key: dealerKey,
            showDmsServiceLineEstimates,
            name: dealerName,
            website: dealerWebsite,
            phone: dealerPhone,
            reminderEmailSettings,
            dmsType,
            alternateLanguage,
        },
    } = useKioskState();
    const { transportationOptions } = useTransportationOptions(kioskKey, dealerKey);

    const appointment: IAppointment = store.session.get('tempAppointment');

    const [totalDetailsPrice, setTotalDetailsPrice] = useState<number>(0);
    const [totalXtimePrice, setTotalXtimePrice] = useState<number>(0);
    const [appointmentDetails, setAppointmentDetails] = useState<IServiceDetail[] | undefined>(undefined);
    const [transportationType, setTransportationType] = useState<string | undefined>(undefined);
    const [closingMessage, setClosingMessage] = useState<string | undefined>(undefined);

    const apptDate: string = appointment ? formatAppointmentDate(appointment) : '';
    const dealerHours: ReminderHours[] | null = reminderEmailSettings ? reminderEmailSettings.hours : null;

    useEffect(() => {
        if (!!appointment && !!appointment.transportation && !!transportationOptions && (closingMessage === undefined || transportationType === undefined)) {
            const selected: LocalizedTransportationOption | undefined = transportationOptions.find((option: LocalizedTransportationOption) => {
                if (appointment.transportation) {
                    return option.type === appointment.transportation.type && option.subtype === (appointment.transportation.subtype || TransportSubtype.none);
                }
                else {
                    return undefined;
                }
            });

            if (selected !== undefined) {
                setClosingMessage(selected.localization.closingMessage ?? '');
                setTransportationType(selected.name ?? '');
            }
        }
    }, [appointment, closingMessage, transportationOptions, transportationType]);

    useEffect(() => {
        if (!appointmentDetails && (appointment.dmsAppointmentKey || appointment.dmsAppointmentId)) {
            getServiceDetails(kioskKey, dealerKey, appointment.dmsAppointmentKey ?? '', appointment.key)
                .then((details) => {
                    setTotalDetailsPrice(details.reduce((accumulator: number, detail: IServiceDetail) => {
                        return accumulator + detail.price;
                    }, 0));
                    setAppointmentDetails(details);
                });
        }
    }, [kioskKey, dealerKey, appointment, appointmentDetails]);

    useEffect(() => {
        if (!totalXtimePrice && appointment.xtimeServices) {
            setTotalXtimePrice(appointment.xtimeServices.reduce((accumulator: number, service: Pick<IXtimeServiceBase, XTimeServiceProperties>) => {
                return accumulator + service.price;
            }, 0));
        }
    }, [appointment, totalXtimePrice]);

    return (
        <AppointmentProvider appointment={appointment}>
            <ScrollableBody
                hideAccessibilityButton
                heading={
                    <Heading
                        primaryHeading={Localization.CheckInComplete}
                        roundedIcon={
                            <GMRoundedIcon
                                roundedBackgroundSize={'large'}
                                themeType={'secondary'}
                            >
                                <GMSizedIcon iconType={'Complete'} iconSize={'medium'} />
                            </GMRoundedIcon>
                        }
                    />
                }
            >
                <GMGrid
                    container
                    className={css.checkInDetailsLanding}
                    xs={12}
                    padding={1}
                    rowGap={2}
                >
                    <GMGrid
                        container
                        xs={12}
                        rowGap={1}
                    >
                        <GMGrid xs={12} paddingLeft={1}>
                            <p>
                                {Localization.formatString(Localization.HiCustomerCheckInComplete, <GMAppointmentName />)}
                            </p>
                        </GMGrid>
                        {closingMessage ? (
                            <GMGrid xs={12} paddingLeft={1}>
                                <p>
                                    {closingMessage}
                                </p>
                            </GMGrid>
                        ) : null}
                    </GMGrid>
                    <GMGrid
                        container
                        className={css.appointmentInfo}
                        xs={12}
                    >
                        <GMGrid
                            container
                            className={css.appointmentHeading}
                            xs={12}
                        >
                            <GMGrid
                                container
                                className={css.headingText}
                                xs={12}
                                rowGap={0.5}
                            >
                                <GMGrid xs={12}>
                                    <strong>
                                        {apptDate}
                                    </strong>
                                </GMGrid>
                                <GMGrid xs={12}>
                                    <p>
                                        {displayLocalizedVehicle(appointment, true)}
                                    </p>
                                </GMGrid>
                            </GMGrid>
                        </GMGrid>
                        <GMGrid
                            container
                            xs={12}
                            className={css.appointmentBody}
                        >
                            {appointment.serviceAdvisorName || transportationType ? (
                                <GMGrid
                                    container
                                    className={css.appointmentAdvisor}
                                    xs={12}
                                    rowGap={0.5}
                                    marginBottom={2}
                                >
                                    {appointment.serviceAdvisorName ? (
                                        <GMGrid xs={12}>
                                            <p>
                                                {Localization.formatString(Localization.Advisor, appointment.serviceAdvisorName)}
                                            </p>
                                        </GMGrid>
                                    ) : null}
                                    {transportationType ? (
                                        <GMGrid xs={12}>
                                            <GMCapitalize>
                                                {Localization.formatString(Localization.Transportation, transportationType)}
                                            </GMCapitalize>
                                        </GMGrid>
                                    ) : null}
                                </GMGrid>
                            ) : null}
                            <GMGrid xs={12}>
                                <Spacer />
                            </GMGrid>
                            <GMGrid
                                container
                                xs={12}
                                marginTop={2}
                            >
                                {appointmentDetails?.length ? (
                                    <GMGrid
                                        container
                                        xs={12}
                                        rowGap={0.5}
                                        marginBottom={2}
                                    >
                                        <GMGrid xs={12} textAlign={'start'}>
                                            <strong>
                                                {Localization.ServicesDescription}
                                            </strong>
                                        </GMGrid>
                                        <GMGrid
                                            container
                                            xs={12}
                                            display={'flex'}
                                            flexDirection={'column'}
                                            rowGap={0.5}
                                        >
                                            {appointmentDetails.map((detail: IServiceDetail) => {
                                                return (
                                                    <GMGrid
                                                        key={`appointment-details-${detail.key}`}
                                                        container
                                                        xs={12}
                                                        wrap={'nowrap'}
                                                    >
                                                        <GMGrid
                                                            xs={9}
                                                            paddingLeft={2}
                                                            textAlign={'start'}
                                                        >
                                                            {dmsType === DMSType.DEALERTRACK ? detail.opCodeDescription : detail.description ?? detail.opCodeDescription}
                                                        </GMGrid>
                                                        {showDmsServiceLineEstimates ? (
                                                            <GMGrid xs={3} textAlign={'end'}>
                                                                {formatAsCurrency(detail.price)}
                                                            </GMGrid>
                                                        ) : null}
                                                    </GMGrid>
                                                );
                                            })}
                                            {showDmsServiceLineEstimates ? (
                                                <GMGrid
                                                    container
                                                    xs={12}
                                                    wrap={'nowrap'}
                                                >
                                                    <GMGrid
                                                        xs={9}
                                                        paddingLeft={1}
                                                        textAlign={'start'}
                                                    >
                                                        {Localization.EstimatedTotal}
                                                    </GMGrid>
                                                    <GMGrid xs={3} textAlign={'end'}>
                                                        <strong>
                                                            {formatAsCurrency(totalDetailsPrice)}
                                                        </strong>
                                                    </GMGrid>
                                                </GMGrid>
                                            ) : null}
                                        </GMGrid>
                                    </GMGrid>
                                ) : null}
                                {!appointment.servicesConfirmed?.length
                                    && !appointment.maintenanceMenusConfirmed?.length
                                    && !appointment.rndServices?.length
                                    && !appointment.xtimeServices?.length ? null :
                                    (
                                        <GMGrid
                                            container
                                            xs={12}
                                            rowGap={0.5}
                                            marginBottom={2}
                                        >
                                            <GMGrid xs={12} textAlign={'start'}>
                                                <strong>
                                                    {Localization.ItemsRequested}
                                                </strong>
                                            </GMGrid>
                                            <GMGrid
                                                container
                                                xs={12}
                                                display={'flex'}
                                                flexDirection={'column'}
                                                rowGap={0.5}
                                            >
                                                {appointment.servicesConfirmed ?
                                                    appointment.servicesConfirmed
                                                        .filter((item: ServiceComment) => {
                                                            return !transportationOptions?.find((option: LocalizedTransportationOption) => {
                                                                return option.opCode === item.opCode;
                                                            });
                                                        })
                                                        .map((item: ServiceComment) => {
                                                            return (
                                                                <>
                                                                    <GMGrid
                                                                        key={`appointment-services-confirmed-${item.id}`}
                                                                        container
                                                                        xs={12}
                                                                        wrap={'nowrap'}
                                                                    >
                                                                        <GMGrid
                                                                            xs={9}
                                                                            paddingLeft={1}
                                                                            textAlign={'start'}
                                                                        >
                                                                            {Locale === alternateLanguage && !!item.nameAltLang ? (
                                                                                item.nameAltLang
                                                                            ) : (
                                                                                item.name
                                                                            )}
                                                                        </GMGrid>
                                                                        {showDmsServiceLineEstimates ? (
                                                                            <GMGrid xs={3} textAlign={'end'}>
                                                                                <SpgPriceText
                                                                                    spgPricing={item.spgPricing ?? undefined}
                                                                                    priceLow={item.priceLow ?? undefined}
                                                                                    priceHigh={item.priceHigh ?? undefined}
                                                                                    price={item.price ?? undefined}
                                                                                    useSpgPricingLogic={!!item.spgPricing}
                                                                                />
                                                                            </GMGrid>
                                                                        ) : null}
                                                                    </GMGrid>
                                                                    <GMGrid
                                                                        xs={12}
                                                                        paddingLeft={2}
                                                                        textAlign={'start'}
                                                                    >
                                                                        {Locale === alternateLanguage && !!item.descriptionAltLang ? (
                                                                            item.descriptionAltLang
                                                                        ) : (
                                                                            item.description
                                                                        )}
                                                                    </GMGrid>
                                                                </>
                                                            );
                                                        }) : null}
                                                {appointment.xtimeServices && appointment.xtimeServices.map((xtimeItem: Pick<IXtimeServiceBase, XTimeServiceProperties>) => {
                                                    return (
                                                        <>
                                                            <GMGrid
                                                                key={`xtime-services-${xtimeItem.id}`}
                                                                container
                                                                xs={12}
                                                                wrap={'nowrap'}
                                                            >
                                                                <GMGrid
                                                                    xs={9}
                                                                    paddingLeft={1}
                                                                    textAlign={'start'}
                                                                >
                                                                    {xtimeItem.name}
                                                                </GMGrid>
                                                                {showDmsServiceLineEstimates ? (
                                                                    <GMGrid xs={3} textAlign={'end'}>
                                                                        <SpgPriceText
                                                                            price={xtimeItem.price ?? undefined}
                                                                            useSpgPricingLogic={false}
                                                                        />
                                                                    </GMGrid>
                                                                ) : null}
                                                            </GMGrid>
                                                            <GMGrid
                                                                xs={12}
                                                                paddingLeft={2}
                                                                textAlign={'start'}
                                                            >
                                                                {xtimeItem.description}
                                                            </GMGrid>
                                                        </>
                                                    );
                                                })}
                                                {appointment.maintenanceMenusConfirmed && appointment.maintenanceMenusConfirmed.map((maintenanceMenuItem: MaintenanceMenu) => {
                                                    return (
                                                        <>
                                                            <GMGrid
                                                                key={`appointment-maintenance-menu-confirmed-${maintenanceMenuItem.id}`}
                                                                container
                                                                xs={12}
                                                                wrap={'nowrap'}
                                                            >
                                                                <GMGrid
                                                                    xs={9}
                                                                    paddingLeft={1}
                                                                    textAlign={'start'}
                                                                >
                                                                    {maintenanceMenuItem.title}
                                                                </GMGrid>
                                                                {showDmsServiceLineEstimates ? (
                                                                    <GMGrid xs={3} textAlign={'end'}>
                                                                        <SpgPriceText
                                                                            spgPricing={maintenanceMenuItem.spgPricing ?? undefined}
                                                                            priceLow={maintenanceMenuItem.priceLow ?? undefined}
                                                                            priceHigh={maintenanceMenuItem.priceHigh ?? undefined}
                                                                            useSpgPricingLogic={!!maintenanceMenuItem.spgPricing}
                                                                        />
                                                                    </GMGrid>
                                                                ) : null}
                                                            </GMGrid>
                                                            <GMGrid
                                                                xs={12}
                                                                paddingLeft={2}
                                                                textAlign={'start'}
                                                            >
                                                                {maintenanceMenuItem.opDescriptions}
                                                            </GMGrid>
                                                        </>
                                                    );
                                                })}
                                                {appointment.rndServices && appointment.rndServices.filter((rndService: RNDService) => {
                                                    return !rndService.doneElsewhere;
                                                })
                                                    .map((rndService: RNDService) => {
                                                        const localizedService = rndService.linkedService ? serviceFactory(rndService.linkedService) : undefined;

                                                        return (
                                                            <>
                                                                <GMGrid
                                                                    key={`appointment-rnd-services-${rndService.opCode}`}
                                                                    container
                                                                    xs={12}
                                                                    wrap={'nowrap'}
                                                                >
                                                                    <GMGrid
                                                                        xs={9}
                                                                        paddingLeft={1}
                                                                        textAlign={'start'}
                                                                    >
                                                                        {getRndServiceDisplayName(rndService, localizedService)}
                                                                    </GMGrid>
                                                                    {showDmsServiceLineEstimates ? (
                                                                        <GMGrid xs={3} textAlign={'end'}>
                                                                            <SpgPriceText
                                                                                spgPricing={!!rndService.linkedService ? rndService.linkedService.spgPricing : undefined}
                                                                                priceLow={!!rndService.linkedService ? rndService.linkedService.priceLow : undefined}
                                                                                priceHigh={!!rndService.linkedService ? rndService.linkedService.priceHigh : undefined}
                                                                                price={!!rndService.price ? rndService.price : undefined}
                                                                                useSpgPricingLogic={!!rndService.linkedService ? !!rndService.linkedService.spgPricing : undefined}
                                                                            />
                                                                        </GMGrid>
                                                                    ) : null}
                                                                </GMGrid>
                                                                {!!rndService.linkedService ? (
                                                                    <GMGrid
                                                                        xs={12}
                                                                        paddingLeft={2}
                                                                        textAlign={'start'}
                                                                    >
                                                                        {Locale === alternateLanguage && !!rndService.linkedService.descriptionAltLang ? (
                                                                            rndService.linkedService.descriptionAltLang
                                                                        ) : (
                                                                            rndService.linkedService.description
                                                                        )}
                                                                    </GMGrid>
                                                                ) : null}
                                                            </>
                                                        );
                                                    })}
                                                {showDmsServiceLineEstimates ? (
                                                    <GMGrid
                                                        container
                                                        xs={12}
                                                        wrap={'nowrap'}
                                                    >
                                                        <GMGrid
                                                            xs={9}
                                                            paddingLeft={1}
                                                            textAlign={'start'}
                                                        >
                                                            {Localization.MinimumEstTotal}
                                                        </GMGrid>
                                                        <GMGrid xs={3} textAlign={'end'}>
                                                            <strong>
                                                                {!!appointment.paymentEstimate || !!totalDetailsPrice || !!totalXtimePrice ? (
                                                                    formatAsCurrency((appointment.paymentEstimate ?? 0) + totalDetailsPrice + totalXtimePrice)
                                                                ) : (
                                                                    Localization.AskUsAboutPricing
                                                                )}
                                                            </strong>
                                                        </GMGrid>
                                                    </GMGrid>
                                                ) : null}
                                            </GMGrid>
                                        </GMGrid>
                                    )}
                                {appointment.comment ? (
                                    <GMGrid
                                        container
                                        xs={12}
                                        rowGap={0.5}
                                    >
                                        <GMGrid xs={12} textAlign={'start'}>
                                            <strong>
                                                {Localization.Comments}
                                            </strong>
                                        </GMGrid>
                                        <GMGrid
                                            xs={12}
                                            paddingLeft={1}
                                            textAlign={'start'}
                                        >
                                            <p>
                                                {appointment.comment}
                                            </p>
                                        </GMGrid>
                                    </GMGrid>
                                ) : null}
                            </GMGrid>
                        </GMGrid>
                    </GMGrid>
                    <GMGrid
                        xs={12}
                        paddingX={1}
                        textAlign={'start'}
                    >
                        <em>
                            {Localization.PriceProvidedDisclaimer}
                        </em>
                    </GMGrid>
                    <GMGrid
                        container
                        xs={12}
                        rowGap={0.5}
                        textAlign={'center'}
                    >
                        <GMGrid xs={12}>
                            <strong>
                                {dealerName}
                            </strong>
                        </GMGrid>
                        <GMGrid xs={12}>
                            <p>
                                {formatAsPhoneNumber(dealerPhone)}
                            </p>
                        </GMGrid>
                    </GMGrid>
                    <GMGrid xs={12} textAlign={'center'}>
                        <GMLink
                            href={dealerWebsite}
                            ariaLabel={Localization.ClickHereToVisitTheDealersWebsite}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                        >
                            {dealerWebsite}
                        </GMLink>
                    </GMGrid>
                    {dealerHours !== null ? (
                        <GMGrid
                            container
                            className={css.dealerHours}
                            xs={12}
                            rowGap={0.5}
                            textAlign={'center'}
                        >
                            <GMGrid xs={12}>
                                <strong>
                                    {Localization.DealershipHours}
                                </strong>
                            </GMGrid>
                            <GMGrid xs={12}>
                                <table>
                                    {dealerHours.map((hour: ReminderHours) => {
                                        return (
                                            <tr key={`dealer-hours-table-${hour.dayOfWeek}`}>
                                                <td className={css.leftCol}>
                                                    {localizedDayOfWeek(hour.dayOfWeek)} {':\r'}
                                                </td>
                                                <td className={css.rightCol}>
                                                    {!hour.startTime || !hour.endTime ? (
                                                        <GMUppercase>
                                                            {Localization.Closed}
                                                        </GMUppercase>
                                                    ) : (
                                                        <div>
                                                            {convertHoursTimeString(hour.startTime, appointment?.locale)}
                                                            {' '}
                                                            {appointment.locale !== Locales.fr ? '-' : 'à'}
                                                            {' '}
                                                            {convertHoursTimeString(hour.endTime, appointment?.locale)}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </table>
                            </GMGrid>
                        </GMGrid>
                    ) : null}
                </GMGrid>
            </ScrollableBody>
        </AppointmentProvider>
    );
};